import { useState, useEffect } from "react";
import { usersAPI, companiesAPI } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Users, Shield, UserCircle } from "lucide-react";

const roleLabels = {
  superadmin: { label: "Super Admin", color: "bg-red-500" },
  admin: { label: "Admin", color: "bg-blue-500" },
  vertriebler: { label: "Vertriebler", color: "bg-green-500" }
};

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    first_name: "",
    last_name: "",
    role: "vertriebler",
    company_ids: []
  });

  const isSuperAdmin = currentUser?.role === "superadmin";

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [usersRes, companiesRes] = await Promise.all([
        usersAPI.list(),
        companiesAPI.list()
      ]);
      setUsers(usersRes.data);
      setCompanies(companiesRes.data);
    } catch (error) {
      console.error("Failed to load data:", error);
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.first_name || !formData.last_name) {
      toast.error("Bitte füllen Sie alle Pflichtfelder aus");
      return;
    }
    
    if (!editingUser && !formData.password) {
      toast.error("Bitte geben Sie ein Passwort ein");
      return;
    }
    
    try {
      if (editingUser) {
        await usersAPI.update(editingUser.id, formData);
        toast.success("Benutzer aktualisiert");
      } else {
        await usersAPI.create(formData);
        toast.success("Benutzer erstellt");
      }
      
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error("Failed to save user:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Speichern");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Möchten Sie diesen Benutzer wirklich löschen?")) return;
    
    try {
      await usersAPI.delete(id);
      toast.success("Benutzer gelöscht");
      loadData();
    } catch (error) {
      console.error("Failed to delete:", error);
      toast.error("Fehler beim Löschen");
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: "",
      first_name: user.first_name,
      last_name: user.last_name,
      role: user.role,
      company_ids: user.company_ids || []
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingUser(null);
    setFormData({
      email: "",
      password: "",
      first_name: "",
      last_name: "",
      role: "vertriebler",
      company_ids: []
    });
  };

  const toggleCompany = (companyId) => {
    setFormData(prev => ({
      ...prev,
      company_ids: prev.company_ids.includes(companyId)
        ? prev.company_ids.filter(id => id !== companyId)
        : [...prev.company_ids, companyId]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-in" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope']">Benutzer</h1>
          <p className="text-muted-foreground">
            Verwalten Sie Benutzer und Berechtigungen
          </p>
        </div>
        <Button onClick={() => { resetForm(); setDialogOpen(true); }} data-testid="new-user-btn">
          <Plus className="h-4 w-4 mr-2" />
          Neuer Benutzer
        </Button>
      </div>

      {/* Users Table */}
      {users.length > 0 ? (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>E-Mail</TableHead>
                  <TableHead>Rolle</TableHead>
                  <TableHead className="hidden md:table-cell">Firmen</TableHead>
                  <TableHead className="hidden sm:table-cell">Status</TableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center">
                            <span className="text-xs font-semibold text-primary">
                              {user.first_name?.[0]}{user.last_name?.[0]}
                            </span>
                          </div>
                          {/* Online Status Indicator */}
                          <div 
                            className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-white ${
                              user.is_online ? 'bg-green-500' : 'bg-gray-300'
                            }`}
                            title={user.is_online ? 'Online' : 'Offline'}
                          />
                        </div>
                        <div>
                          <span className="font-medium">
                            {user.first_name} {user.last_name}
                          </span>
                          {user.is_online && (
                            <span className="ml-2 text-xs text-green-600">online</span>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {user.email}
                    </TableCell>
                    <TableCell>
                      <Badge className={roleLabels[user.role]?.color}>
                        {roleLabels[user.role]?.label || user.role}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <div className="flex flex-wrap gap-1">
                        {user.company_ids?.map(companyId => {
                          const company = companies.find(c => c.id === companyId);
                          return company ? (
                            <Badge key={companyId} variant="outline" className="text-xs">
                              {company.name}
                            </Badge>
                          ) : null;
                        })}
                      </div>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <div className="flex items-center gap-2">
                        <div 
                          className={`h-2.5 w-2.5 rounded-full ${
                            user.is_online ? 'bg-green-500' : 'bg-gray-300'
                          }`}
                        />
                        <span className={`text-sm ${user.is_online ? 'text-green-600' : 'text-gray-500'}`}>
                          {user.is_online ? 'Online' : 'Offline'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(user)}
                          disabled={user.role === "superadmin" && !isSuperAdmin}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(user.id)}
                          className="text-destructive"
                          disabled={user.id === currentUser?.id || (user.role === "superadmin" && !isSuperAdmin)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        <Card className="py-12">
          <CardContent className="text-center">
            <Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-lg font-semibold mb-2">Keine Benutzer gefunden</h3>
            <p className="text-muted-foreground mb-4">
              Legen Sie Ihren ersten Benutzer an
            </p>
            <Button onClick={() => { resetForm(); setDialogOpen(true); }}>
              <Plus className="h-4 w-4 mr-2" />
              Neuer Benutzer
            </Button>
          </CardContent>
        </Card>
      )}

      {/* User Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">
              {editingUser ? "Benutzer bearbeiten" : "Neuer Benutzer"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Vorname *</Label>
                <Input
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  placeholder="Max"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Nachname *</Label>
                <Input
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  placeholder="Mustermann"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>E-Mail *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="max@beispiel.de"
                required
              />
            </div>

            <div className="space-y-2">
              <Label>{editingUser ? "Neues Passwort (optional)" : "Passwort *"}</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="••••••••"
                required={!editingUser}
              />
            </div>

            <div className="space-y-2">
              <Label>Rolle *</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => setFormData({ ...formData, role: value })}
                disabled={!isSuperAdmin && formData.role === "superadmin"}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {isSuperAdmin && (
                    <SelectItem value="superadmin">Super Admin</SelectItem>
                  )}
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="vertriebler">Vertriebler</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {companies.length > 0 && formData.role !== "superadmin" && (
              <div className="space-y-2">
                <Label>Firmen-Zuordnung</Label>
                <div className="border rounded-lg p-3 space-y-2 max-h-40 overflow-y-auto">
                  {companies.map((company) => (
                    <div key={company.id} className="flex items-center gap-2">
                      <Checkbox
                        id={company.id}
                        checked={formData.company_ids.includes(company.id)}
                        onCheckedChange={() => toggleCompany(company.id)}
                      />
                      <label htmlFor={company.id} className="text-sm cursor-pointer">
                        {company.name}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Abbrechen
              </Button>
              <Button type="submit">
                {editingUser ? "Speichern" : "Erstellen"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
